import { type RefObject, useCallback, useEffect, useState } from 'react';
import { cx } from '../cx';

/**
 * THE PAN AFFORDANCE: a scroll region that pans SIDEWAYS says so at the edge it is
 * hiding content behind, and says nothing at an edge that hides nothing.
 *
 * PROVENANCE (W5 B-3): derived from hig:
 * https://developer.apple.com/design/human-interface-guidelines/scroll-views - "Because
 * scroll indicators aren't always visible, it can be helpful to make it obvious when
 * content extends beyond the view" - and recorded as a deviation in the
 * `docs/ios26-mechanisms.md` row "Scroll edge effect, INLINE axis (the pan affordance)",
 * which is where the departure from `.scroll-edge`'s recipe is written down. It is the
 * SECOND of the two cues that page names, not a replacement for the first: the system
 * scroll indicator stays exactly as the system draws it (review check "the table keeps the
 * system scroll bar and paints the trailing edge at rest", `list-behaviour.spec.ts`),
 * because "the scroll view itself has no appearance, but it can display a translucent
 * scroll indicator that typically appears after people begin scrolling the view's content".
 *
 * ONE ANSWER FOR TWO SURFACES (audit P2-7, and the carry-over waves 2 and 3 both
 * declined to close alone). The table's right edge and the board's right edge are the
 * same complaint - a value or a card cut mid-string against a bare strip, with nothing
 * on screen saying there is more - and two answers to one complaint is how a board
 * starts looking like a different product from the table.
 *
 * IT IS NOT THE `ScrollEdge` MECHANISM, and the difference is deliberate. `.scroll-edge`
 * is the iOS 26 effect for content passing under CHROME: five progressive
 * `backdrop-filter` bands plus a tint in the canvas colour. Both halves are wrong here.
 * DESIGN.md forbids blur under data ("DATA SURFACES ... crisp and opaque. No blur, no
 * gradients, no glass"), and the tint would be the wrong colour anyway - this edge sits
 * on a card, not on the canvas. So this is the same IDEA (an edge that guards passing
 * content) built crisp: one soft shading strip, no blur, at 12% in light and 55% in dark,
 * over a 1rem inset. The deviation is recorded in `docs/ios26-mechanisms.md`.
 *
 * IT IS A STATE, NOT A STYLE. The strip is painted only while that side actually has
 * more to reach - which is what makes it an affordance rather than decoration, and is
 * why DESIGN.md's "no gradients on data surfaces" is not the rule it lands under. A
 * table that fits its card shows nothing at all.
 *
 * THE HOST MUST BE POSITIONED AND MUST NOT BE THE SCROLLER. A strip inside the scrolling
 * element pans away with the content it is guarding; the strips are siblings of the
 * scroller inside a `relative` box that wraps it.
 */
export interface PanEdgeState {
  /** There is content to reach by panning toward the start (left, in LTR). */
  readonly start: boolean;
  /** There is content to reach by panning toward the end (right, in LTR). */
  readonly end: boolean;
}

/**
 * ONE PIXEL OF TOLERANCE. Browsers report fractional scroll offsets on a zoomed or
 * fractionally sized element, so `scrollLeft + clientWidth === scrollWidth` is a
 * comparison that is false at the end of the pan roughly half the time.
 */
const EDGE_EPSILON = 1;

export function panEdgesOf(el: {
  scrollLeft: number;
  clientWidth: number;
  scrollWidth: number;
}): PanEdgeState {
  return {
    start: el.scrollLeft > EDGE_EPSILON,
    end: el.scrollLeft + el.clientWidth < el.scrollWidth - EDGE_EPSILON,
  };
}

/**
 * Watches a scroll region and reports which sides still hold content.
 *
 * THREE THINGS CHANGE THE ANSWER, so all three are listened to: the user pans
 * (`scroll`), the region is resized (the window, the sidebar, a phone rotating), and the
 * CONTENT is resized - a column shown or hidden, a page of rows arriving. The last one
 * is the one a `scroll` listener alone misses, and it is the common case in this product:
 * a table whose twelfth column is toggled off stops overflowing without anybody scrolling.
 */
export function usePanEdges(ref: RefObject<HTMLElement | null>): PanEdgeState {
  const [edges, setEdges] = useState<PanEdgeState>({ start: false, end: false });

  const measure = useCallback(() => {
    const el = ref.current;
    if (el === null) return;
    const next = panEdgesOf(el);
    setEdges((current) =>
      current.start === next.start && current.end === next.end ? current : next,
    );
  }, [ref]);

  useEffect(() => {
    const el = ref.current;
    if (el === null) return;
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    for (const child of el.children) observer.observe(child);
    return () => {
      el.removeEventListener('scroll', measure);
      observer.disconnect();
    };
  }, [ref, measure]);

  return edges;
}

export interface PanEdgesProps extends PanEdgeState {
  /**
   * Where the strips stop. The table's scrollport has a footer bar under it and the
   * board has none, so the host decides; the default covers the whole host.
   */
  readonly className?: string | undefined;
  /**
   * How far in from the start edge the start strip stands, in px: a table with its first
   * column pinned shades the pinned column's trailing edge, where the content passes under it
   * (BR-PARTS, DESIGN.md C10). Absent or 0 keeps the strip at the host's own edge.
   */
  readonly startInset?: number | undefined;
}

export function PanEdges({ start, end, className, startInset }: PanEdgesProps) {
  return (
    /* `aria-hidden` and `pointer-events-none`: it is a picture of a scroll position that
       a screen reader already has a better answer for, and a strip that swallowed clicks
       would make the last visible column unclickable - which is the defect, not the fix. */
    <div aria-hidden className={cx('pointer-events-none absolute inset-0', className)}>
      <div
        className={cx('pan-edge pan-edge-start', start && 'pan-edge-on')}
        {...(startInset === undefined || startInset === 0
          ? {}
          : { style: { insetInlineStart: `${String(startInset)}px` } })}
      />
      <div className={cx('pan-edge pan-edge-end', end && 'pan-edge-on')} />
    </div>
  );
}
